import { useState, useEffect, useMemo, useCallback } from "react";
import { useRecordContext, useNotify } from "react-admin";
import {
  Box,
  Grid2 as Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Skeleton,
  FormHelperText,
  Button,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import { usePandaDocConfig, PandaDocConfigUpdate } from "@/hooks/usePandaDocConfig";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { Partner, PandaDocConfig, PandaDocFolder, PandaDocIsolationMode } from "@/types";

/** Map server config to local editable state */
const configToLocal = (cfg: PandaDocConfig | null): PandaDocConfigUpdate => ({
  isolation_mode: cfg?.isolation_mode || "",
  template_folder_uuid: cfg?.template_folder_uuid || cfg?.folder_uuid || "",
  document_folder_uuid: cfg?.document_folder_uuid || cfg?.folder_uuid || "",
  credential_ref: cfg?.credential_ref || "",
  webhook_secret_ref: cfg?.webhook_secret_ref || "",
});

// Field metadata for labels and tooltips
const FIELD_METADATA = {
  template_folder_uuid: {
    label: "Template Folder",
    tooltip: "PandaDoc folder containing this partner's document templates",
  },
  document_folder_uuid: {
    label: "Document Folder",
    tooltip: "PandaDoc folder where signed documents are stored",
  },
  isolation_mode: {
    label: "Isolation Mode",
    tooltip: "How documents are isolated within PandaDoc",
  },
  credential_ref: {
    label: "API Key Variable",
    tooltip: "Environment variable name for this partner's PandaDoc API key (e.g. PANDADOC_API_KEY_WSPARTNER)",
  },
  webhook_secret_ref: {
    label: "Webhook Secret Variable",
    tooltip:
      "Environment variable name for this partner's PandaDoc webhook secret (e.g. PANDADOC_WEBHOOK_SECRET_WSPARTNER)",
  },
  workspace_document_folder: {
    label: "Document Folder (optional)",
    tooltip: "Optional folder for organizing documents within the workspace",
  },
};

const ISOLATION_MODES = [
  { id: "folder", name: "Folder" },
  { id: "workspace", name: "Workspace" },
];

interface EditableFieldProps {
  label: string;
  value: string;
  helperText?: string;
  onSave: (value: string) => void;
  disabled: boolean;
  readOnly?: boolean;
  type?: "text" | "select";
  options?: { id: string; name: string }[];
}

const EditableField = ({
  label,
  value,
  helperText,
  onSave,
  disabled,
  readOnly = false,
  type = "text",
  options = [],
}: EditableFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");

  useEffect(() => {
    setEditValue(value || "");
  }, [value]);

  const handleSave = () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value || "");
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && type === "text") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (readOnly) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1">{value || "-"}</Typography>
        {helperText && (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        )}
      </Box>
    );
  }

  if (isEditing) {
    return (
      <Box>
        {type === "select" ? (
          <FormControl fullWidth size="small">
            <InputLabel>{label}</InputLabel>
            <Select value={editValue} onChange={(e) => setEditValue(e.target.value)} label={label} autoFocus>
              {options.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth
            size="small"
            label={label}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        )}
        <Box sx={{ mt: 1, display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={handleSave} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {helperText && <FormHelperText sx={{ mt: 0.5 }}>{helperText}</FormHelperText>}
      </Box>
    );
  }

  return (
    <Box
      onClick={() => !disabled && setIsEditing(true)}
      sx={{
        cursor: disabled ? "wait" : "pointer",
        "&:hover": { bgcolor: disabled ? "transparent" : "action.hover" },
        borderRadius: 1,
        p: 1,
        ml: -1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="body1">{value || "-"}</Typography>
        </Box>
        {!disabled && <EditIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.5 }} />}
      </Box>
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

interface FolderFieldProps {
  label: string;
  value: string;
  helperText?: string;
  onSave: (value: string) => void;
  disabled: boolean;
  readOnly?: boolean;
  folders: PandaDocFolder[];
  isLoadingFolders: boolean;
  onLoadFolders: () => void;
}

const FolderField = ({
  label,
  value,
  helperText,
  onSave,
  disabled,
  readOnly = false,
  folders,
  isLoadingFolders,
  onLoadFolders,
}: FolderFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");

  useEffect(() => {
    setEditValue(value || "");
  }, [value]);

  const handleSave = () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value || "");
    setIsEditing(false);
  };

  const handleStartEditing = () => {
    if (folders.length === 0 && !isLoadingFolders) {
      onLoadFolders();
    }
    setIsEditing(true);
  };

  // Find folder name for display
  const selectedFolder = folders.find((f) => f.id === value);
  const displayValue = selectedFolder?.name || value || "-";

  if (readOnly) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1">{displayValue}</Typography>
        {helperText && (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        )}
      </Box>
    );
  }

  if (isEditing) {
    return (
      <Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <FormControl fullWidth size="small">
            <InputLabel>{label}</InputLabel>
            <Select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              label={label}
              disabled={isLoadingFolders}
              autoFocus
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {folders.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh folders">
            <IconButton size="small" onClick={onLoadFolders} disabled={isLoadingFolders}>
              {isLoadingFolders ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ mt: 1, display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={handleSave} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {helperText && <FormHelperText sx={{ mt: 0.5 }}>{helperText}</FormHelperText>}
        {folders.length === 0 && !isLoadingFolders && (
          <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
            No folders found. Click refresh to load folders from PandaDoc.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      onClick={() => !disabled && handleStartEditing()}
      sx={{
        cursor: disabled ? "wait" : "pointer",
        "&:hover": { bgcolor: disabled ? "transparent" : "action.hover" },
        borderRadius: 1,
        p: 1,
        ml: -1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="body1">{displayValue}</Typography>
          {value && selectedFolder && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              UUID: {value}
            </Typography>
          )}
        </Box>
        {!disabled && <EditIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.5 }} />}
      </Box>
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export const PandaDocConfigSection = () => {
  const record = useRecordContext<Partner>();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();

  const partnerId = record?.id?.toString() || "";

  // Don't render or fetch if no partner ID (e.g., on list page or before record loads)
  const shouldFetch = Boolean(partnerId);

  const {
    config,
    templateFolders,
    documentFolders,
    isLoading,
    isUpdating,
    isLoadingTemplateFolders,
    isLoadingDocumentFolders,
    error,
    updateConfig,
    fetchTemplateFolders,
    fetchDocumentFolders,
  } = usePandaDocConfig({ partnerId, enabled: shouldFetch });

  // Local state for batch editing
  const [localConfig, setLocalConfig] = useState<PandaDocConfigUpdate>({});

  // Sync server config into local state
  useEffect(() => {
    if (config) {
      setLocalConfig(configToLocal(config));
    }
  }, [config]);

  const isDirty = useMemo(() => {
    if (!config) return false;
    const server = configToLocal(config);
    return (Object.keys(server) as (keyof PandaDocConfigUpdate)[]).some((k) => localConfig[k] !== server[k]);
  }, [localConfig, config]);

  const handleFieldChange = useCallback((field: keyof PandaDocConfigUpdate, value: string) => {
    setLocalConfig((prev) => {
      const next = { ...prev, [field]: value };
      // Clear mode-specific fields when switching isolation mode.
      // document_folder_uuid is intentionally preserved — both modes use it.
      if (field === "isolation_mode") {
        if (value === PandaDocIsolationMode.WORKSPACE) {
          next.template_folder_uuid = "";
        } else if (value === PandaDocIsolationMode.FOLDER) {
          next.credential_ref = "";
          next.webhook_secret_ref = "";
        }
      }
      return next;
    });
  }, []);

  // Warn on browser tab close / external navigation
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleSave = async () => {
    try {
      await updateConfig(localConfig);
      notify("PandaDoc configuration updated", { type: "success" });
    } catch (error) {
      notifyError(error);
    }
  };

  const handleDiscard = () => {
    if (config) {
      setLocalConfig(configToLocal(config));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="100%" height={32} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Failed to load PandaDoc configuration: {error.message}</Alert>
      </Box>
    );
  }

  const isFolderMode = localConfig.isolation_mode === PandaDocIsolationMode.FOLDER;
  const isWorkspaceMode = localConfig.isolation_mode === PandaDocIsolationMode.WORKSPACE;

  const isIncompleteFolder = isFolderMode && (!localConfig.template_folder_uuid || !localConfig.document_folder_uuid);
  const isIncompleteWorkspace = isWorkspaceMode && (!localConfig.credential_ref || !localConfig.webhook_secret_ref);

  return (
    <Box sx={{ p: 2 }}>
      {isIncompleteFolder && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Folder isolation requires both a template folder and document folder to be configured before documents can be
          created.
        </Alert>
      )}
      {isIncompleteWorkspace && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Workspace isolation requires both an API key variable and webhook secret variable to be configured before
          documents can be created.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <EditableField
            label={FIELD_METADATA.isolation_mode.label}
            value={localConfig.isolation_mode || ""}
            helperText={FIELD_METADATA.isolation_mode.tooltip}
            onSave={(value) => handleFieldChange("isolation_mode", value)}
            disabled={isUpdating}
            type="select"
            options={ISOLATION_MODES}
          />
        </Grid>

        {isFolderMode && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
              <FolderField
                label={`${FIELD_METADATA.template_folder_uuid.label} *`}
                value={localConfig.template_folder_uuid || ""}
                helperText={FIELD_METADATA.template_folder_uuid.tooltip}
                onSave={(value) => handleFieldChange("template_folder_uuid", value)}
                disabled={isUpdating}
                folders={templateFolders}
                isLoadingFolders={isLoadingTemplateFolders}
                onLoadFolders={fetchTemplateFolders}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
              <FolderField
                label={`${FIELD_METADATA.document_folder_uuid.label} *`}
                value={localConfig.document_folder_uuid || ""}
                helperText={FIELD_METADATA.document_folder_uuid.tooltip}
                onSave={(value) => handleFieldChange("document_folder_uuid", value)}
                disabled={isUpdating}
                folders={documentFolders}
                isLoadingFolders={isLoadingDocumentFolders}
                onLoadFolders={fetchDocumentFolders}
              />
            </Grid>
          </>
        )}

        {isWorkspaceMode && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
              <EditableField
                label={`${FIELD_METADATA.credential_ref.label} *`}
                value={localConfig.credential_ref || ""}
                helperText={FIELD_METADATA.credential_ref.tooltip}
                onSave={(value) => handleFieldChange("credential_ref", value)}
                disabled={isUpdating}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
              <EditableField
                label={`${FIELD_METADATA.webhook_secret_ref.label} *`}
                value={localConfig.webhook_secret_ref || ""}
                helperText={FIELD_METADATA.webhook_secret_ref.tooltip}
                onSave={(value) => handleFieldChange("webhook_secret_ref", value)}
                disabled={isUpdating}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
              <FolderField
                label={FIELD_METADATA.workspace_document_folder.label}
                value={localConfig.document_folder_uuid || ""}
                helperText={FIELD_METADATA.workspace_document_folder.tooltip}
                onSave={(value) => handleFieldChange("document_folder_uuid", value)}
                disabled={isUpdating}
                folders={documentFolders}
                isLoadingFolders={isLoadingDocumentFolders}
                onLoadFolders={fetchDocumentFolders}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Save / Discard bar */}
      {isDirty && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            display: "flex",
            alignItems: "center",
            gap: 2,
            pt: 2,
            mt: 2,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            zIndex: 10,
          }}
        >
          <Tooltip
            title="Complete all required fields before saving"
            disableHoverListener={!(isIncompleteFolder || isIncompleteWorkspace)}
          >
            <span>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isUpdating || isIncompleteFolder || isIncompleteWorkspace}
              >
                {isUpdating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Save Changes
              </Button>
            </span>
          </Tooltip>
          <Button variant="text" onClick={handleDiscard} disabled={isUpdating}>
            Discard
          </Button>
        </Box>
      )}
    </Box>
  );
};
